'use client';

import { useRecurly } from '@recurly/react-recurly';
import { useEffect, useState, useCallback, useRef, JSX } from 'react';

import { LoggerFactory } from '@videoblocks/typescript-logger';

import { GooglePayService } from './GooglePayService';
import { getBraintreeClientAuthorization } from './actions';

export type GooglePayCheckoutProps = {
    onSuccess: (data: GooglePaySuccessData) => void;
    onError: (error: { code: string; message: string; cause?: unknown }) => void;
    disabled?: boolean;
    className?: string;
    currency?: string;
    total?: string;
};

export type GooglePaySuccessData = {
    tokenId: string;
    firstName?: string;
    lastName?: string;
};

export type GooglePayToken = {
    id: string;
    email: string;
};

export type GooglePayError = {
    code: string;
    message: string;
    cause?: unknown;
};

const logger = LoggerFactory.create('GooglePayCheckout');

export default function GooglePayCheckout({
    onSuccess,
    onError,
    disabled = false,
    className = '',
    currency = 'USD',
    total = '0',
}: GooglePayCheckoutProps): JSX.Element {
    const googlePayConfig = GooglePayService.getConfig();
    const recurly = useRecurly();
    const [isLoading, setIsLoading] = useState(false);
    const [isInitialized, setIsInitialized] = useState(false);
    const [isReady, setIsReady] = useState(false);
    const [clientAuthorization, setClientAuthorization] = useState<string | undefined>(undefined);
    const containerRef = useRef<HTMLDivElement>(null);

    const initializeGooglePay = useCallback(() => {
        if (isInitialized) return;

        try {
            const googlePay = GooglePayService.createGooglePayInstance(recurly, clientAuthorization, currency, total);

            /**
             * This event is emitted when any error is encountered, whether during setup of the Google Pay payment sheet, or during payment authorization.
             * It will be useful to display errors to your customer if a problem occurs during the Google Pay flow.
             */
            googlePay.on('error', (err: GooglePayError) => {
                setIsLoading(false);

                // Check if this is a cancellation (user closed the payment sheet)
                const errorMessage = err.message || '';
                const errorCode = err.code || '';

                // Common cancellation indicators in Google Pay errors
                const isCancellation =
                    errorMessage.toLowerCase().includes('could not get the payment data') ||
                    errorMessage.toLowerCase().includes('user cancelled') ||
                    errorMessage.toLowerCase().includes('user canceled') ||
                    errorMessage.toLowerCase().includes('payment cancelled') ||
                    errorMessage.toLowerCase().includes('payment canceled') ||
                    errorCode.toLowerCase().includes('cancel');

                if (isCancellation) {
                    // Don't show an error for user cancellation - it's a normal flow
                    return;
                }

                // Replace generic initialization errors with more user-friendly messages
                let displayMessage = errorMessage || 'An error occurred with Google Pay';
                if (
                    displayMessage.toLowerCase().includes('failed to initialize') ||
                    displayMessage.toLowerCase().includes('initialization failed')
                ) {
                    displayMessage = 'Google Pay is not available on this device';
                }

                onError({
                    code: err.code || 'GOOGLE_PAY_ERROR',
                    message: displayMessage,
                    cause: err.cause,
                });
            });

            /**
             * This event is fired when the customer has completed the Google Pay payment sheet flow.
             * Recurly has received the payment details, and generated this token to be used in our API.
             * Using this as FALLBACK token source in case paymentAuthorized doesn't provide token
             */
            googlePay.on('token', (token: GooglePayToken) => {
                // Only use this token if paymentAuthorized hasn't already processed
                if (isLoading) {
                    setIsLoading(false);

                    // Note: In fallback mode, we don't have access to billing data from paymentData
                    // So we'll use fallback values for name fields
                    const successData: GooglePaySuccessData = {
                        tokenId: token.id,
                        firstName: 'Google', // Fallback when billing data unavailable
                        lastName: 'Pay', // Fallback when billing data unavailable
                    };

                    if (googlePayConfig.useMockGooglePay) {
                        onSuccess(successData);
                        return;
                    }

                    onSuccess(successData);
                }
            });

            // Note: Google Pay doesn't have a 'cancel' event like PayPal
            // Cancellation is handled through the error event

            /**
             * This event is fired when the GooglePay instance has completed initialization and the Customer is ready to Pay.
             */
            googlePay.on('ready', (googlePayButton: HTMLButtonElement | undefined) => {
                // First set ready state to remove skeleton loader
                setIsReady(true);

                // Then append button with a small delay to ensure React has removed skeleton
                setTimeout(() => {
                    const container = containerRef.current;

                    if (container && googlePayButton) {
                        // Add click event listener to set loading state
                        if (typeof googlePayButton.addEventListener === 'function') {
                            googlePayButton.addEventListener('click', () => {
                                setIsLoading(true);
                            });
                        }

                        // Check if button is already in container (prevent duplicates)
                        if (!container.contains(googlePayButton)) {
                            container.appendChild(googlePayButton);
                        }
                    }
                }, 50); // Small delay to ensure React has updated DOM
            });

            /**
             * This event is fired when the customer has authorized the payment presented on the Google Pay payment sheet.
             * paymentData contains the Recurly token that can be extracted via paymentData.recurly.token
             * Using this as PRIMARY token source for faster response
             */
            googlePay.on(
                'paymentAuthorized',
                (paymentData: {
                    recurlyToken: GooglePayToken;
                    paymentMethodData: {
                        info: {
                            billingAddress: {
                                name: string;
                            };
                        };
                    };
                }) => {
                    // Extract and USE token from paymentAuthorized (PRIMARY)
                    try {
                        const token = paymentData?.recurlyToken;
                        if (token) {
                            // Extract billing name from Google Pay data
                            const billingAddress = paymentData?.paymentMethodData?.info?.billingAddress;
                            const fullName = billingAddress?.name || '';
                            const nameParts = fullName.split(' ');
                            const firstName = nameParts[0] || '';
                            const lastName = nameParts.slice(1).join(' ') || '';

                            setIsLoading(false);

                            const successData: GooglePaySuccessData = {
                                tokenId: token.id,
                                firstName: firstName,
                                lastName: lastName,
                            };

                            if (googlePayConfig.useMockGooglePay) {
                                onSuccess(successData);
                                return;
                            }

                            onSuccess(successData);
                            return;
                        }
                    } catch (error) {
                        // Will fall back to token event
                    }
                },
            );

            // Note: Only supporting the defined event types: error, token, ready, paymentAuthorized

            // Note: Cannot access private _callbacks property for debugging

            setIsInitialized(true);
        } catch (error) {
            onError({
                code: 'INIT_ERROR',
                message: 'Failed to initialize Google Pay (1)',
                cause: error,
            });
        }
    }, [
        isInitialized,
        isLoading,
        googlePayConfig.useMockGooglePay,
        recurly,
        clientAuthorization,
        currency,
        total,
        onSuccess,
        onError,
    ]);

    // Fetch client authorization when component mounts (only if not using mock)
    useEffect(() => {
        if (!googlePayConfig.useMockGooglePay && !clientAuthorization) {
            getBraintreeClientAuthorization()
                .then(setClientAuthorization)
                .catch(() => {
                    onError({
                        code: 'AUTH_ERROR',
                        message: 'Failed to initialize Google Pay (2). Please try again.',
                    });
                });
        }
    }, [googlePayConfig.useMockGooglePay, clientAuthorization, onError]);

    useEffect(() => {
        if (googlePayConfig.useMockGooglePay || (recurly && clientAuthorization && !isInitialized)) {
            // eslint-disable-next-line react-hooks/set-state-in-effect
            initializeGooglePay();
        }
    }, [
        initializeGooglePay,
        googlePayConfig.useMockGooglePay,
        recurly,
        clientAuthorization,
        isInitialized,
        onSuccess,
        onError,
    ]);

    // Fallback timeout to prevent infinite loading
    useEffect(() => {
        const timeout = setTimeout(() => {
            if (!isReady && !googlePayConfig.useMockGooglePay && isInitialized) {
                // Initialization timeout - force ready state
                setIsReady(true);
            }
        }, 10000); // 10 second timeout

        return () => clearTimeout(timeout);
    }, [isReady, isInitialized, googlePayConfig.useMockGooglePay]);

    // Always render the container immediately - show skeleton loader while button is loading
    const showSkeleton = !isInitialized || !isReady || isLoading;
    return (
        <div className={`w-full ${className}`}>
            <div ref={containerRef} id="google-pay-button-container" className="google-pay-button-wrapper">
                {/* Show skeleton loader while button is initializing */}
                {showSkeleton && (
                    <div
                        className="w-full h-[38px] bg-gray-100 rounded animate-pulse flex items-center justify-center"
                        aria-label="Loading Google Pay button"
                        aria-live="polite"
                    >
                        <span className="text-xs text-gray-400">Loading...</span>
                    </div>
                )}
                {/* Container will be populated with Google Pay button when ready */}
            </div>
        </div>
    );
}
